import { useState } from 'react';
import { classNames } from 'primereact/utils';
import { DictationControl, DictationControlProps } from './DictationControl';
import { ToolbarChipButton } from './ToolbarChipButton';

export const COMMON_SYMBOLS = [
  '→',
  '←',
  '↑',
  '↓',
  '↔',
  '•',
  '–',
  '—',
  '…',
  '△',
  'Ø',
  '~',
  '√',
  '²',
  'c̄',
  's̄',
  'ā',
  'p̄',
  'Ψ',
  '†',
  '‡',
  '°',
  '±',
  '≈',
  '≠',
];

interface SymbolPickerToolbarProps {
  onInsertSymbol: (symbol: string) => void;
  /**
   * Optional dictation controls. When passed, a microphone button appears
   * to the right of the symbol picker (see DictationControl for the full
   * idle / listening visual). Kept opt-in so existing callers (the
   * timeline chronology inline editor) don't gain a mic button they
   * weren't asking for.
   */
  dictation?: DictationControlProps;
  /**
   * `bordered` (default true) wraps the toolbar in its own top-only bordered
   * container — the historical look used by inline chronology editors where
   * the toolbar sits above a separate textarea.
   *
   * Set to `false` when the toolbar is nested inside a shared bordered
   * wrapper alongside the textarea, so the outer container can own the
   * single border/focus-within ring and the toolbar is just an inline row
   * of controls at the top of the field.
   */
  bordered?: boolean;
}

export const SymbolPickerToolbar = ({ onInsertSymbol, dictation, bordered = true }: SymbolPickerToolbarProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className={classNames(
        // 8px padding on all sides. The textarea below zeroes its own top
        // padding (see `!pt-0` at each usage site) so this pb-2 is the sole
        // gap between the chips and the first line of text — no 16px stack.
        'flex shrink-0 items-center gap-2 bg-white p-2',
        bordered && 'rounded-t-[4px] border border-b-0 border-gray-300',
      )}
    >
      <div className="relative">
        <ToolbarChipButton onClick={() => setIsOpen((prev) => !prev)} title="Insert symbol" aria-label="Insert symbol">
          <span className="text-[16px] leading-none">Ω</span>
        </ToolbarChipButton>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-[98]" onClick={() => setIsOpen(false)} />
            <div
              className="absolute top-full left-0 z-[99] mt-1 rounded-[4px] border border-gray-200 bg-white p-2 shadow-lg"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 32px)', gap: '4px' }}
            >
              {COMMON_SYMBOLS.map((sym) => (
                <button
                  key={sym}
                  type="button"
                  className="flex h-[32px] w-[32px] items-center justify-center rounded text-[16px] hover:bg-green-50 focus:outline-none"
                  onClick={() => {
                    onInsertSymbol(sym);
                    setIsOpen(false);
                  }}
                  title={sym}
                >
                  {sym}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {dictation && <DictationControl {...dictation} />}
    </div>
  );
};
